import React, { useCallback } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from 'primereact/button';
import { Editor } from 'primereact/editor';
import { InputText } from 'primereact/inputtext';
import { classNames } from 'primereact/utils';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useCreateReportRequestMutation } from '@/app/services/issues';
import { addToast } from '@/app/slices/toast';
import { ToastLife } from '@/components/hoc/toast-container/enums';

import { baseIssueSchema, ReportRequestValues } from './schema';

import './ReportRequest.css';

type ReportRequestProps = {
  className?: string;
};

export const ReportRequest = ({ className }: ReportRequestProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ReportRequestValues>({
    resolver: zodResolver(baseIssueSchema),
  });

  const [createReportRequest] = useCreateReportRequestMutation();

  const onSubmit = useCallback(
    async (values: ReportRequestValues) => {
      try {
        const res = await createReportRequest({ input: values });
        dispatch(
          addToast({
            severity: 'success',
            summary: 'Report Submitted',
            detail: 'The report was submitted successfully.',
            life: ToastLife.DEFAULT,
          }),
        );

        if (res.data) {
          navigate(`/issues/${res.data.createReportRequest.id}`);
        }
      } catch (error) {
        dispatch(
          addToast({
            severity: 'error',
            summary: 'Submission Failed',
            detail: 'An error occurred while submitting the report.' + error,
            life: ToastLife.DEFAULT,
          }),
        );
      }
    },
    [createReportRequest, dispatch, navigate],
  );

  return (
    <div className={`report-request ${className || ''}`}>
      <div className="issue-type-picker" />

      <form onSubmit={handleSubmit(onSubmit)} className="report-request-form">
        <div className="report-request-form__fields">
          <div className="report-request-form__field">
            <label htmlFor="title">Title</label>
            <Controller
              name="title"
              control={control}
              render={({ field, fieldState }) => (
                <InputText
                  id={field.name}
                  {...field}
                  className={classNames({ 'p-invalid': fieldState.error })}
                />
              )}
            />
            {errors.title && (
              <div className="errors">{errors.title.message}</div>
            )}
          </div>

          <div className="report-request-form__field">
            <label htmlFor="content">Description</label>
            <Controller
              name="content"
              control={control}
              render={({ field, fieldState }) => (
                <Editor
                  id={field.name}
                  {...field}
                  className={classNames('issues-editor', {
                    'p-invalid': fieldState.error,
                  })}
                  onTextChange={(e) => field.onChange(e.htmlValue)}
                />
              )}
            />
            {errors.content && (
              <div className="errors">{errors.content.message}</div>
            )}
          </div>
        </div>

        <div className="report-request-form__submit">
          <Button
            icon="pi pi-check"
            label="Submit"
            type="submit"
            className="submit-button"
          />
        </div>
      </form>
    </div>
  );
};
